import { Button } from "@/components/ui/button";
import * as Sentry from "@sentry/react";
import type { ErrorInfo } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";

interface ErrorBoundaryState {
  hasError: boolean;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error);
    Sentry.captureMessage("Error caught in ErrorBoundary", {
      level: "error",
      extra: { errorInfo },
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback resetError={() => this.setState({ hasError: false })} />;
    }

    return this.props.children;
  }
}

const ErrorFallback: React.FC<{ resetError: () => void }> = ({ resetError }) => {
  const navigate = useNavigate();

  const handleGoHome = () => navigate("/dashboard");

  return (
    <div className="error-fallback flex h-screen flex-col items-center justify-center bg-gray-50 p-6">
      <h2 className="text-2xl font-bold text-red-600">Something went wrong!</h2>
      <p className="mt-2 text-center text-sm text-gray-600">
        We are sorry for the inconvenience. Please try again or go back to the beginning.
      </p>
      <div className="mt-6 w-full max-w-sm space-y-4">
        <Button onClick={handleGoHome} variant="destructive" className="w-full">
          Go home
        </Button>
        <Button onClick={resetError} variant="secondary" className="w-full">
          Try again
        </Button>
      </div>
    </div>
  );
};

export default ErrorBoundary;
